import { useDataStore } from '../stores/dataStore.js';
import { useFilterStore } from '../stores/filterStore.js';
import { useLoadingStore } from '../stores/loadingStore.js';

export class StatusBar {
  constructor(container, options = {}) {
    this.container = container;
    this.options = {
      showFilterCount: true,
      showMineCount: true,
      showLoadingStatus: true,
      updateInterval: 1000, // ms
      ...options
    };
    this.stats = {
      totalMines: 0,
      visibleMines: 0,
      activeFilters: 0,
      isLoading: false,
      lastUpdate: null
    };
    this.unsubscribes = [];
    this.updateTimer = null;
    this.init();
  }

  init() {
    this.render();

    // Only subscribe to stores if in browser environment
    if (typeof window !== 'undefined') {
      this.subscribeToStores();
      this.startUpdateTimer();
    }
  }

  render() {
    if (!this.container) {
      // Create status bar if container not provided
      this.container = this.createStatusBarElement();
    }

    this.container.innerHTML = `
      <div class="status-bar-content">
        ${this.options.showMineCount ? `
        <div class="status-item" id="mineCount">
          <i class="fas fa-mountain status-icon"></i>
          <span class="status-label">Mines:</span>
          <span class="status-value" id="mineCountValue">0</span>
        </div>
        ` : ''}

        ${this.options.showFilterCount ? `
        <div class="status-item" id="filterCount">
          <i class="fas fa-filter status-icon"></i>
          <span class="status-label">Filters:</span>
          <span class="status-value" id="filterCountValue">0</span>
        </div>
        ` : ''}

        ${this.options.showLoadingStatus ? `
        <div class="status-item" id="loadingStatus">
          <i class="fas fa-circle status-icon status-indicator" id="statusIndicator"></i>
          <span class="status-value" id="loadingStatusValue">Ready</span>
        </div>
        ` : ''}

        <div class="status-item" id="lastUpdate">
          <i class="fas fa-clock status-icon"></i>
          <span class="status-value" id="lastUpdateValue">Never</span>
        </div>
      </div>
    `;
  }

  createStatusBarElement() {
    const statusBar = document.createElement('div');
    statusBar.className = 'status-bar';
    statusBar.id = 'statusBar';

    // Add to DOM (typically in footer)
    const footer = document.getElementById('footer') || document.body;
    footer.appendChild(statusBar);

    return statusBar;
  }

  subscribeToStores() {
    // Subscribe to data store for mine counts
    this.unsubscribes.push(
      useDataStore.subscribe(
        (state) => ({
          totalMines: state.totalMines,
          visibleMines: state.mines?.length || 0,
          lastFetch: state.lastFetch
        }),
        (data) => {
          this.stats.totalMines = data.totalMines;
          this.stats.visibleMines = data.visibleMines;
          this.stats.lastUpdate = data.lastFetch;
          this.updateDisplay();
        }
      )
    );

    // Subscribe to filter store for active filter count
    this.unsubscribes.push(
      useFilterStore.subscribe(
        (state) => state.filters,
        (filters) => {
          this.stats.activeFilters = this.countActiveFilters(filters);
          this.updateDisplay();
        }
      )
    );

    // Subscribe to loading store for loading status
    this.unsubscribes.push(
      useLoadingStore.subscribe(
        (state) => ({
          isLoading: state.isLoading,
          loadingText: state.loadingText
        }),
        (loading) => {
          this.stats.isLoading = loading.isLoading;
          this.stats.loadingText = loading.loadingText;
          this.updateDisplay();
        }
      )
    );

    // Initialize with current values
    this.stats.totalMines = useDataStore.getState().totalMines;
    this.stats.visibleMines = useDataStore.getState().mines?.length || 0;
    this.stats.activeFilters = this.countActiveFilters(useFilterStore.getState().filters);
    this.stats.isLoading = useLoadingStore.getState().isLoading;
    this.stats.lastUpdate = useDataStore.getState().lastFetch;

    this.updateDisplay();
  }

  countActiveFilters(filters) {
    let count = 0;
    Object.values(filters).forEach(filterValue => {
      if (Array.isArray(filterValue) && filterValue.length > 0) {
        count++;
      } else if (filterValue && typeof filterValue === 'string') {
        count++;
      } else if (filterValue && typeof filterValue === 'boolean') {
        count++;
      }
    });
    return count;
  }

  updateDisplay() {
    if (this.options.showMineCount) {
      this.updateMineCount();
    }

    if (this.options.showFilterCount) {
      this.updateFilterCount();
    }

    if (this.options.showLoadingStatus) {
      this.updateLoadingStatus();
    }

    this.updateLastUpdateTime();
  }

  updateMineCount() {
    const element = this.container.querySelector('#mineCountValue');
    if (element) {
      const text = this.stats.activeFilters > 0
        ? `${this.stats.visibleMines} of ${this.stats.totalMines}`
        : this.stats.totalMines.toString();

      element.textContent = this.formatNumber(text);

      // Add visual indicator if filtered
      const parent = element.closest('.status-item');
      if (parent) {
        parent.classList.toggle('filtered', this.stats.activeFilters > 0);
      }
    }
  }

  updateFilterCount() {
    const element = this.container.querySelector('#filterCountValue');
    if (element) {
      element.textContent = this.stats.activeFilters.toString();

      // Visual indicator for active filters
      const parent = element.closest('.status-item');
      if (parent) {
        parent.classList.toggle('active', this.stats.activeFilters > 0);
      }
    }
  }

  updateLoadingStatus() {
    const statusElement = this.container.querySelector('#loadingStatusValue');
    const indicatorElement = this.container.querySelector('#statusIndicator');

    if (statusElement && indicatorElement) {
      if (this.stats.isLoading) {
        statusElement.textContent = this.stats.loadingText || 'Loading...';
        indicatorElement.className = 'fas fa-circle status-icon status-indicator loading';
      } else {
        statusElement.textContent = 'Ready';
        indicatorElement.className = 'fas fa-circle status-icon status-indicator ready';
      }
    }
  }

  updateLastUpdateTime() {
    const element = this.container.querySelector('#lastUpdateValue');
    if (element && this.stats.lastUpdate) {
      const timeAgo = this.formatTimeAgo(this.stats.lastUpdate);
      element.textContent = timeAgo;
      element.title = new Date(this.stats.lastUpdate).toLocaleString();
    }
  }

  formatNumber(value) {
    if (typeof value === 'string') return value;
    return new Intl.NumberFormat().format(value);
  }

  formatTimeAgo(timestamp) {
    if (!timestamp) return 'Never';

    const now = Date.now();
    const diff = now - timestamp;
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (seconds < 60) {
      return 'Just now';
    } else if (minutes < 60) {
      return `${minutes}m ago`;
    } else if (hours < 24) {
      return `${hours}h ago`;
    } else {
      const days = Math.floor(hours / 24);
      return `${days}d ago`;
    }
  }

  startUpdateTimer() {
    this.updateTimer = setInterval(() => {
      this.updateLastUpdateTime();
    }, this.options.updateInterval);
  }

  stopUpdateTimer() {
    if (this.updateTimer) {
      clearInterval(this.updateTimer);
      this.updateTimer = null;
    }
  }

  // Manual update methods for external use
  setMineCount(total, visible = null) {
    this.stats.totalMines = total;
    if (visible !== null) {
      this.stats.visibleMines = visible;
    }
    this.updateDisplay();
  }

  setLoadingStatus(isLoading, text = null) {
    this.stats.isLoading = isLoading;
    if (text) {
      this.stats.loadingText = text;
    }
    this.updateDisplay();
  }

  markDataUpdate() {
    this.stats.lastUpdate = Date.now();
    this.updateDisplay();
  }

  // Show temporary status messages
  showMessage(message, duration = 3000, type = 'info') {
    const messageElement = document.createElement('div');
    messageElement.className = `status-message status-message-${type}`;
    messageElement.textContent = message;

    this.container.appendChild(messageElement);

    // Animate in
    setTimeout(() => {
      messageElement.classList.add('visible');
    }, 10);

    // Remove after duration
    setTimeout(() => {
      messageElement.classList.remove('visible');
      setTimeout(() => {
        if (messageElement.parentNode) {
          messageElement.parentNode.removeChild(messageElement);
        }
      }, 300);
    }, duration);
  }

  getStats() {
    return { ...this.stats };
  }

  destroy() {
    this.stopUpdateTimer();

    // Unsubscribe from stores
    this.unsubscribes.forEach(unsubscribe => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    });
    this.unsubscribes = [];

    if (this.container && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
    }
  }
}